import { useMemo, useState } from "react";
import OddsBtn from "@/components/oddsBtn/OddsBtn";
import {
  SPORTSBOOK_BETSLIP_CTA_CLICKED,
  SPORTSBOOK_ODDS_SELECTED,
  SPORTSBOOK_READY_TO_BET_MODAL_CLOSED,
} from "@/constants/amplitude";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import { Maybe, OddsSelection } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useArticleInfo } from "@/utils/hooks/useArticleContext";
import { Team } from "../tempTypes";
import styles from "./teamProps.module.scss";

interface TeamsLineProps {
  homeTeam: Team;
  awayTeam: Team;
  sessionId: number | undefined;
  deviceId: string | undefined;
  eventName: string | undefined;
  selections: OddsSelection[];
  isFeaturedBet: boolean;
  authorName?: string;
  sportName?: string;
}
const TeamsLine = ({
  homeTeam,
  awayTeam,
  eventName,
  selections,
  isFeaturedBet = false,
  authorName,
  sportName,
}: TeamsLineProps) => {
  const { title, sport, author } = useArticleInfo();
  const [url, setUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { logAmplitudeEvent } = useAmplitude();

  const parsedAuthorName = author ?? authorName;
  const parsedSport = sportName ?? sport;

  const ampProps = useMemo(
    () => ({
      Module: isFeaturedBet ? "featured_bet" : "coupon",
      "Event Name": eventName ?? "",
      Competition: parsedSport ?? "",
      "Article Title": title ?? "",
      Author: parsedAuthorName ?? "",
    }),
    [title, eventName, parsedAuthorName, isFeaturedBet, parsedSport]
  );

  const onOddsClick = (redirectUrl?: Maybe<string>) => {
    if (redirectUrl) {
      setUrl(redirectUrl);
      logAmplitudeEvent(SPORTSBOOK_ODDS_SELECTED, { ...ampProps });
      setShowModal(true);
    }
  };

  const onClickPrimaryCta = () => {
    logAmplitudeEvent(SPORTSBOOK_BETSLIP_CTA_CLICKED, {
      ...ampProps,
      "Link Text": "go_to_betslip",
      "Link URL": url,
      Module: "ready_to_bet_modal",
    });
  };

  const onRequestClose = () => {
    logAmplitudeEvent(SPORTSBOOK_READY_TO_BET_MODAL_CLOSED, {
      ...ampProps,
      Module: "ready_to_bet_modal",
    });
    setShowModal(false);
  };

  const redirectToSportsbookProps = {
    sportsbookRedirectUrl: url,
    isOpen: showModal,
    onClickPrimaryCta,
    onRequestClose,
  };

  const awayTeamHasNumberFireIds =
    selections?.[0]?.teamNumberFireId != null && awayTeam?.numberFireId != null;
  const selectionMatchesAwayTeam =
    selections?.[0]?.teamNumberFireId === awayTeam?.numberFireId;
  const awayTeamLinkOrText =
    awayTeamHasNumberFireIds && selectionMatchesAwayTeam && selections?.[1] ? (
      <a href={awayTeam.url}>{awayTeam.name}</a>
    ) : (
      selections?.[0]?.runnerName
    );

  const homeTeamHasNumberFireIds =
    selections?.[1]?.teamNumberFireId != null && homeTeam?.numberFireId != null;
  const selectionMatchesHomeTeam =
    selections?.[1]?.teamNumberFireId === homeTeam?.numberFireId;
  const homeTeamLinkOrText =
    homeTeamHasNumberFireIds && selectionMatchesHomeTeam ? (
      <a href={homeTeam.url}>{homeTeam.name}</a>
    ) : (
      selections?.[1]?.runnerName
    );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.teamsContainer}>
          <div className={styles.teamInfo}>{awayTeamLinkOrText}</div>
          {selections?.[1] ? (
            <>
              <div className={styles.separator}>
                <span>@</span>
                <hr />
              </div>
              <div className={styles.teamInfo}>{homeTeamLinkOrText}</div>
            </>
          ) : null}
        </div>
        <div className={styles.betContainer}>
          <div className={styles.betLinks}>
            {awayTeam.propBets.map((bet) => (
              <OddsBtn
                handicap={
                  bet.selections?.[0]?.formattedHandicap ??
                  bet.selections?.[0]?.handicap ??
                  ""
                }
                classButton={styles.oddsBtn}
                classHandicap={styles.handicap}
                onClick={() => onOddsClick(bet.selections?.[0]?.url)}
                odds={bet.selections?.[0]?.odds ?? ""}
                key={`${bet.selections?.[0]?.url}  ${bet.selections?.[0]?.odds}  ${bet.selections?.[0]?.handicap}`}
              />
            ))}
          </div>
          <div className={styles.betLinks}>
            {homeTeam.propBets.map((bet) =>
              bet.selections?.[1] ? (
                <OddsBtn
                  handicap={
                    bet.selections[1].formattedHandicap ??
                    bet.selections[1].handicap ??
                    ""
                  }
                  classButton={styles.oddsBtn}
                  classHandicap={styles.handicap}
                  onClick={() => onOddsClick(bet.selections?.[1]?.url)}
                  odds={bet.selections[1].odds ?? ""}
                  key={
                    bet.selections?.[1].url +
                    (bet.selections?.[1].odds ?? "odds") +
                    bet.selections?.[1].handicap
                  }
                />
              ) : (
                ""
              )
            )}
          </div>
        </div>
      </div>
      <RedirectToSportsbook {...redirectToSportsbookProps} />
    </>
  );
};

export default TeamsLine;
